import ShelfController from '../../core/shelf.controller';
import dayjs from 'dayjs';
import { User } from '../../data';
import { canAccess } from '../../core/access';
import { downloadExcel } from '../../utils/excel';
import { fmtCapture } from '../../utils/timeUtils';
import { SOURCE_LABELS, DEFAULT_FILTERS, formatPriceName, filtersToQuery, isCanceled } from './levantamientoShared';

class LevantamientoGeneralController extends ShelfController {
    async initialize() {
        if (!this.isAuthenticated) {
            this.setState({ loading: false });
            this.navigateLogin();
            return;
        }

        const user = User.getInstance();
        if (!canAccess(user?.userType, 'levantamiento-general')) {
            this.setState({ loading: false, accessDenied: true });
            return;
        }

        try {
            const [clients, tagsRes, levFacets, storesRes, users] = await Promise.all([
                this.data.fetchClients().catch(() => []),
                this.data.fetchTags().catch(() => null),
                this.data.fetchLevantamientoFacets().catch(() => null),
                this.data.fetchStoresCached().catch(() => null),
                this.data.fetchUsers().catch(() => [])
            ]);

            const clientOptions = (clients || [])
                .filter((c) => c?._id)
                .map((c) => ({ label: c.name, value: c._id }));
            const storeOptions = (storesRes?.data?.stores || [])
                .filter((s) => s?._id)
                .map((s) => ({ label: s.name, value: s._id }));
            const userOptions = (users || [])
                .filter((u) => u?._id && u?.name)
                .map((u) => ({ label: u.name, value: u._id }));
            // Category/segment/grams/storeType all come from the viewer-gated levantamiento facets,
            // so asesores get the full filter set (no dependency on the admin-only audit facets).
            const categoryOptions = (levFacets?.data?.categories || [])
                .filter(Boolean)
                .map((c) => ({ label: c, value: c }));
            const segmentOptions = (levFacets?.data?.segments || [])
                .filter(Boolean)
                .map((s) => ({ label: s, value: s }));
            // Only tags that can be assigned to a client, user, or store are useful here.
            const tagOptions = (tagsRes?.tags || [])
                .filter((t) => t?._id && (t.applicableModels || []).some((m) => ['Client', 'User', 'Store'].includes(m)))
                .map((t) => ({ label: t.name, value: t._id, color: t.color }));
            const gramsOptions = (levFacets?.data?.grams || []).map((g) => ({ label: `${g} g`, value: g }));
            const storeTypeOptions = (levFacets?.data?.storeTypes || []).filter(Boolean).map((s) => ({ label: s, value: s }));

            this.setState({
                clientOptions,
                storeOptions,
                userOptions,
                categoryOptions,
                segmentOptions,
                tagOptions,
                gramsOptions,
                storeTypeOptions,
                loading: false
            });
            await this.loadRows();
        } catch (err) {
            this.setState({ loading: false, error: true });
        }
    }

    buildParams(extra = {}) {
        const { filters, pagination, dateRange } = this.state;
        return {
            ...filtersToQuery(filters, dateRange),
            // List-only: hide atípicos when the toggle is on (graphs always drop them).
            hideOutliers: filters.hideOutliers ? 'true' : undefined,
            page: pagination.page,
            limit: pagination.limit,
            ...extra
        };
    }

    async loadRows() {
        // Abort any in-flight request and ignore its (stale) response, so rapidly changing a filter
        // always lands on the latest selection instead of an out-of-order earlier response.
        this._rowsAbort?.abort();
        const abort = new AbortController();
        this._rowsAbort = abort;
        this.setState({ dataLoading: true });
        try {
            const res = await this.data.fetchLevantamientoGeneral(this.buildParams(), abort.signal);
            if (this._rowsAbort !== abort) return; // superseded by a newer load
            const rows = res?.data?.rows || [];
            const total = res?.data?.pagination?.total || 0;
            this.setState((prev) => ({ rows, pagination: { ...prev.pagination, total }, dataLoading: false }));
        } catch (err) {
            if (this._rowsAbort !== abort || isCanceled(err)) return; // aborted / superseded
            this.props.onMessage?.('error', 'No se pudo cargar el levantamiento general');
            this.setState({ dataLoading: false });
        }
    }

    setFilter(key, value) {
        this.setState(
            (prev) => ({ filters: { ...prev.filters, [key]: value }, pagination: { ...prev.pagination, page: 1 } }),
            () => this.loadRows()
        );
    }

    setDateRange(dateRange) {
        this.setState(
            (prev) => ({ dateRange, pagination: { ...prev.pagination, page: 1 } }),
            () => this.loadRows()
        );
    }

    setPage(page, pageSize) {
        this.setState(
            (prev) => ({ pagination: { ...prev.pagination, page, limit: pageSize } }),
            () => this.loadRows()
        );
    }

    clearFilters() {
        this.setState(
            { filters: { ...DEFAULT_FILTERS }, pagination: { page: 1, limit: 50, total: 0 } },
            () => this.loadRows()
        );
    }

    // Open the charts screen carrying the current filters as query params.
    openCharts() {
        const { filters, dateRange } = this.state;
        const q = new URLSearchParams(filtersToQuery(filters, dateRange)).toString();
        this.props.navigate(`/dashboard/levantamiento-general/charts${q ? `?${q}` : ''}`);
    }

    // Export progress modal: step + elapsed seconds so a long export doesn't look frozen.
    startExportProgress() {
        this._exportStartedAt = Date.now();
        this.setState({ exporting: true, exportStep: 'fetching', exportElapsed: 0 });
        clearInterval(this._exportTimer);
        this._exportTimer = setInterval(
            () => this.setState({ exportElapsed: Math.floor((Date.now() - this._exportStartedAt) / 1000) }),
            1000
        );
    }

    stopExportProgress() {
        clearInterval(this._exportTimer);
        this._exportTimer = null;
        this.setState({ exporting: false, exportStep: null });
    }

    componentWillUnmount() {
        clearInterval(this._exportTimer);
        this._rowsAbort?.abort();
    }

    async exportExcel() {
        try {
            this.startExportProgress();
            // Export the full filtered set (not just the current page).
            const res = await this.data.fetchLevantamientoGeneral(this.buildParams({ all: 'true', page: undefined, limit: undefined }));
            // The API client resolves 4xx/5xx too — a failed export is an error, not "no rows".
            if (!res?.success) {
                this.props.onMessage?.('error', res?.message || 'No se pudo exportar el Excel');
                this.stopExportProgress();
                return;
            }
            const rows = res?.data?.rows || [];
            if (!rows.length) {
                this.props.onMessage?.('warning', 'No hay registros para exportar con los filtros actuales');
                this.stopExportProgress();
                return;
            }

            const columns = [
                'Producto',
                'Código',
                'Gramos',
                'Fuente',
                'Piso de venta',
                'Tipo de tienda',
                'Cliente',
                'Usuario',
                'Fecha',
                'Rol',
                'Precio (USD)',
                'Atípico'
            ];
            const data = rows.map((r) => ({
                Producto: formatPriceName(r),
                Código: r.barcode || '',
                Gramos: r.grams ?? '',
                Fuente: SOURCE_LABELS[r.source] || r.source,
                'Piso de venta': r.storeName,
                'Tipo de tienda': r.storeType || '',
                Cliente: r.clientName,
                Usuario: r.userName || '',
                Fecha: r.capturedAt ? fmtCapture(r.capturedAt, r.source, 'YYYY-MM-DD HH:mm') : '',
                Rol: r.userType,
                'Precio (USD)': r.priceUsd ?? '',
                Atípico: r.isOutlier ? 'Sí' : 'No'
            }));
            // Building the workbook blocks the UI — let the modal paint the new step first.
            this.setState({ exportStep: 'building', exportRowCount: rows.length });
            await new Promise((resolve) => setTimeout(resolve, 50));
            downloadExcel(columns, data, `LevantamientoGeneral_${dayjs().format('YYYYMMDD')}.xlsx`);
            if (res.data.truncated) {
                this.props.onMessage?.(
                    'warning',
                    `Se exportaron los ${rows.length.toLocaleString('es')} registros más recientes (máximo). Acota las fechas o filtros para exportar el resto.`
                );
            }
            this.stopExportProgress();
        } catch (err) {
            this.props.onMessage?.('error', 'No se pudo exportar el Excel');
            this.stopExportProgress();
        }
    }
}

export default LevantamientoGeneralController;
